import React, { createContext, useContext, useState, ReactNode, useMemo } from "react";
import { MagasinOption, PerformanceView } from "../Performance.model";
import { SelectedFilters } from "../../CALiveFilterBar/CALiveFilterBar";

interface PerformanceContextType {
  view: PerformanceView;
  setView: (view: PerformanceView) => void;
  performanceRange: string;
  setPerformanceRange: (range: string) => void;
  analysisDate: Date | null;
  setAnalysisDate: (date: Date | null) => void;
  comparativeDate: Date | null;
  setComparativeDate: (date: Date | null) => void;
  derivedComparativeDate: Date | null;
  selectedMagasin: MagasinOption | null;
  setSelectedMagasin: (magasin: MagasinOption | null) => void;
  isFilterOpen: boolean;
  setIsFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDayOffEnabled: boolean;
  setIsDayOffEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  resetFilters: () => void;
  isCALive: boolean;
  caLiveFilters?: SelectedFilters;
  isPerformanceLoading: boolean;
  setIsPerformanceLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const PerformanceContext = createContext<PerformanceContextType | undefined>(undefined);

interface PerformanceProviderProps {
  children: ReactNode;
  isCALive?: boolean;
  caLiveFilters?: SelectedFilters;
  initialView?: PerformanceView;
}

export const PerformanceProvider: React.FC<PerformanceProviderProps> = ({ children, isCALive = false, caLiveFilters, initialView = "horaire" }) => {
  const [view, setView] = useState<PerformanceView>(initialView);
  const [performanceRange, setPerformanceRange] = useState<string>("N-1");
  const [analysisDate, setAnalysisDate] = useState<Date | null>(null);
  const [comparativeDate, setComparativeDate] = useState<Date | null>(null);
  const [selectedMagasin, setSelectedMagasin] = useState<MagasinOption | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [isDayOffEnabled, setIsDayOffEnabled] = useState<boolean>(true);
  const [isPerformanceLoading, setIsPerformanceLoading] = useState<boolean>(false);

  const derivedComparativeDate = useMemo(() => {
    if (performanceRange === "CUSTOM") return null;
    const base = analysisDate ?? new Date();
    const date = new Date(base);
    if (performanceRange === "N-1") {
      date.setDate(date.getDate() - (52 * 7));
    } else if (performanceRange === "S-1") {
      date.setDate(date.getDate() - 7);
    }
    return date;
  }, [analysisDate, performanceRange]);

  const resetFilters = () => {
    setSelectedMagasin(null);
  };

  return (
    <PerformanceContext.Provider
      value={{
        view,
        setView,
        performanceRange,
        setPerformanceRange,
        analysisDate,
        setAnalysisDate,
        comparativeDate,
        setComparativeDate,
        derivedComparativeDate,
        selectedMagasin,
        setSelectedMagasin,
        isFilterOpen,
        setIsFilterOpen,
        isDayOffEnabled,
        setIsDayOffEnabled,
        resetFilters,
        isCALive,
        caLiveFilters,
        isPerformanceLoading,
        setIsPerformanceLoading,
      }}
    >
      {children}
    </PerformanceContext.Provider>
  );
};

export const usePerformance = () => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error("usePerformance must be used within a PerformanceProvider");
  }
  return context;
};
